import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flags } from 'react-feature-flags';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import CakeOutlined from '@mui/icons-material/CakeOutlined';
import WcOutlined from '@mui/icons-material/WcOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import QueryStatsOutlined from '@mui/icons-material/QueryStatsOutlined';

import { RiUser3Line, RiFolderInfoLine, RiVideoLine, RiSettings5Line, RiHealthBookLine } from 'react-icons/ri';

import PortalLayout from '../../../../layout/Portal';
import ENVIRONMENT from '../../../../core/constants/environment.constant';
import ROUTES from '../../../../core/constants/routes.constant';
import { FEATURES } from '../../../../core/constants/features.constants';
import { useAppSelector } from '../../../../store';
import { healthDataOptInSelector } from '../../../../store/participant/selectors';
import { chartColours } from '../../../../core/constants/colours.constants';
import { RankingItem } from '../../../../core/types/common.types';
import RankingList from '../../../../components/content/RankingList';
import AgeVerticalBarChart from '../../../../components/content/AgeVerticalBarChart';
import SexIconChart from '../../../../components/content/SexIconChart';
import PortalAction from '../../../../components/content/PortalAction';
import StatCard from '../../../../components/content/StatCard';
import DonutGrid from '../../../../components/content/DonutGrid';
import ParticipantCountCard from '../../../../components/content/ParticipantCountCard';
import TabPanel from '../../../../components/content/TabPanel';
import PageContainer from '../../../../components/content/PageContainer';

const topDepartments: RankingItem[] = [
  { label: 'Oncology', value: 13.13 },
  { label: 'Cardiology', value: 11.44 },
  { label: 'Gynaecology', value: 7.14 },
  { label: 'Vascular Lab', value: 7.08 },
  { label: 'Rheumatology', value: 6.82 },
];

const topDisorders: RankingItem[] = [
  { label: 'High blood pressure', value: 39.62 },
  { label: 'Retrovirus (HIV) infections', value: 23.37 },
  { label: 'Diabetes', value: 18.93 },
  { label: 'High cholesterol or blood fats', value: 15.98 },
  { label: 'Obesity', value: 11.46 },
];

const ageDistribution: RankingItem[] = [
  { label: '18–24', value: 4.41 },
  { label: '25-34', value: 13.65 },
  { label: '35-44', value: 18.32 },
  { label: '45-54', value: 18.5 },
  { label: '55-64', value: 20.11 },
  { label: '65-74', value: 16.62 },
  { label: '75-84', value: 7.23 },
  { label: '85+', value: 1.1 },
];

const sexDistribution: RankingItem[] = [
  { label: 'Female', value: 64.18 },
  { label: 'Male', value: 35.82 },
  { label: 'Intersex', value: 0 },
];

function HomeScene() {
  const { t } = useTranslation('portal');
  const healthDataOptIn = useAppSelector(healthDataOptInSelector);
  const [activeTab, setActiveTab] = useState(0);

  return (
    <PortalLayout>
      <PageContainer padded={false}>
        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: '#EEF3F6',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label={t('home.yourPortalTitle')}
            variant="fullWidth"
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTabs-flexContainer': {
                gap: { xs: 1, md: 1.5 },
              },
              '& .MuiTab-root': {
                minHeight: { xs: 76, md: 86 },
                px: { xs: 1.5, md: 2.5 },
                py: 1.5,
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 900,
                fontSize: { xs: '0.92rem', md: '1rem' },
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.72,
                transition:
                  'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease, opacity 150ms ease',
                '&:hover': {
                  opacity: 1,
                  bgcolor: 'background.paper',
                  borderColor: chartColours[1],
                  boxShadow: 2,
                  transform: 'translateY(-1px)',
                },
                '&.Mui-selected': {
                  opacity: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.dark',
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-selected svg': {
                  color: 'primary.contrastText',
                },
              },
            }}
          >
            <Tab
              icon={
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: activeTab === 0 ? 'rgba(255, 255, 255, 0.18)' : `${chartColours[2]}18`,
                    color: activeTab === 0 ? 'primary.contrastText' : chartColours[2],
                    border: 1,
                    borderColor: activeTab === 0 ? 'rgba(255, 255, 255, 0.35)' : `${chartColours[2]}55`,
                    mb: 0.75,
                  }}
                >
                  <HomeOutlined sx={{ fontSize: 25 }} />
                </Box>
              }
              label={t('home.yourPortalTitle')}
            />

            <Tab
              icon={
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: activeTab === 1 ? 'rgba(255, 255, 255, 0.18)' : `${chartColours[1]}18`,
                    color: activeTab === 1 ? 'primary.contrastText' : chartColours[1],
                    border: 1,
                    borderColor: activeTab === 1 ? 'rgba(255, 255, 255, 0.35)' : `${chartColours[1]}55`,
                    mb: 0.75,
                  }}
                >
                  <QueryStatsOutlined sx={{ fontSize: 25 }} />
                </Box>
              }
              label={t('home.snapshot.title')}
            />
          </Tabs>
        </Box>

        <TabPanel activeTab={activeTab} index={0}>
          <Box
            sx={{
              mb: 3,
              px: { xs: 0.5, md: 0 },
              pt: { xs: 1, md: 2 },
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                maxWidth: 760,
                fontWeight: 900,
                letterSpacing: '-0.06em',
                lineHeight: 1.02,
                color: 'text.primary',
              }}
            >
              {t('home.yourPortalTitle')}
            </Typography>

            <Typography
              variant="h6"
              component="p"
              sx={{
                mt: 1.5,
                maxWidth: 720,
                color: 'text.secondary',
                fontWeight: 500,
                lineHeight: 1.45,
              }}
            >
              {t('home.yourPortalBlurb')}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {healthDataOptIn && (
              <Flags authorizedFlags={[FEATURES.EHR]}>
                <Grid item xs={12} md={6}>
                  <PortalAction
                    title={t('home.healthInfoLink')}
                    description={t('home.healthInfoBlurb')}
                    route={ROUTES.portalHealth}
                    icon={<RiHealthBookLine />}
                    iconColour={chartColours[1]}
                    testId="health-btn"
                  />
                </Grid>
              </Flags>
            )}

            <Flags authorizedFlags={[FEATURES.USER_PROFILE]}>
              <Grid item xs={12} md={6}>
                <PortalAction
                  title={t('home.personalInfoLink')}
                  description={t('home.personalInfoBlurb')}
                  route={ROUTES.portalPersonal}
                  icon={<RiUser3Line />}
                  iconColour={chartColours[2]}
                  testId="personal-btn"
                />
              </Grid>
            </Flags>

            <Flags authorizedFlags={[FEATURES.USER_PREFERENCES]}>
              <Grid item xs={12} md={6}>
                <PortalAction
                  title={t('home.preferencesLink')}
                  description={t('home.preferencesBlurb')}
                  route={ROUTES.portalPrefs}
                  icon={<RiSettings5Line />}
                  iconColour={chartColours[3]}
                  testId="preferences-btn"
                />
              </Grid>
            </Flags>

            <Flags authorizedFlags={[FEATURES.PORTAL_PIS]}>
              <Grid item xs={12} md={6}>
                <PortalAction
                  title={t('home.pisLink')}
                  description={t('home.pisBlurb')}
                  route={ROUTES.portalInfo}
                  icon={<RiFolderInfoLine />}
                  iconColour={chartColours[0]}
                  testId="pis-btn"
                />
              </Grid>
            </Flags>

            <Flags authorizedFlags={[FEATURES.PORTAL_VIDEO]}>
              <Grid item xs={12} md={6}>
                <PortalAction
                  title={t('home.videoLink')}
                  description={t('home.videoBlurb')}
                  route={ROUTES.portalVideo}
                  icon={<RiVideoLine />}
                  iconColour={chartColours[5]}
                  testId="video-btn"
                />
              </Grid>
            </Flags>
          </Grid>
        </TabPanel>

        <TabPanel activeTab={activeTab} index={1}>
          <Paper
            elevation={4}
            sx={{
              mb: 3,
              border: 1,
              borderColor: 'primary.dark',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -70,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: 'info.main',
                opacity: 0.45,
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: -80,
                left: -60,
                width: 180,
                height: 180,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
                opacity: 0.45,
              }}
            />

            <Box sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
              <Chip
                label={t('home.snapshot.title')}
                sx={{
                  fontWeight: 900,
                  mb: 1.5,
                  bgcolor: 'background.paper',
                  color: 'primary.main',
                  border: 1,
                  borderColor: 'rgba(255, 255, 255, 0.45)',
                }}
              />

              <Typography variant="h4" component="h2" sx={{ fontWeight: 900 }}>
                {t('home.snapshot.welcome')}
              </Typography>

              <Typography
                variant="body1"
                component="p"
                sx={{
                  mt: 0.75,
                  maxWidth: 760,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {t('home.snapshot.intro')}
              </Typography>
            </Box>
          </Paper>

          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <ParticipantCountCard
                title={t('home.snapshot.participantsTitle')}
                blurb={t('home.snapshot.participantsBlurb')}
                stat="55k"
                description={t('home.snapshot.participantsDescription')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StatCard
                title={t('home.snapshot.departmentsTitle')}
                subtitle={t('home.snapshot.departmentsBlurb')}
                icon={<ApartmentOutlined />}
                tone="info"
              >
                <RankingList items={topDepartments} tone="info" />
              </StatCard>
            </Grid>

            <Grid item xs={12}>
              <StatCard
                title={t('home.snapshot.sexTitle')}
                subtitle={t('home.snapshot.sexBlurb')}
                icon={<WcOutlined />}
                tone="error"
              >
                <SexIconChart
                  items={sexDistribution}
                  maleLabel={t('home.snapshot.male')}
                  femaleLabel={t('home.snapshot.female')}
                  subText={t('home.snapshot.sexSubNote')}
                />
              </StatCard>
            </Grid>

            <Grid item xs={12}>
              <StatCard
                title={t('home.snapshot.ageTitle')}
                subtitle={t('home.snapshot.ageBlurb')}
                icon={<CakeOutlined />}
                tone="secondary"
              >
                <AgeVerticalBarChart items={ageDistribution} />
              </StatCard>
            </Grid>

            <Grid item xs={12}>
              <StatCard
                title={t('home.snapshot.conditionsTitle')}
                subtitle={t('home.snapshot.conditionsBlurb')}
                icon={<HealthAndSafetyOutlined />}
                tone="primary"
              >
                <DonutGrid items={topDisorders} />
              </StatCard>
            </Grid>
          </Grid>
        </TabPanel>
      </PageContainer>

      {ENVIRONMENT.ENVIRONMENT && (
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" align="center" color="text.secondary" paragraph>
            {ENVIRONMENT.ENVIRONMENT} - Version {ENVIRONMENT.VERSION}
          </Typography>
        </>
      )}
    </PortalLayout>
  );
}

export default HomeScene;
